import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Trash2, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export default function AdminMaintenance() {
  const { isSuperAdmin } = useAuth();
  const [orderCount, setOrderCount] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const loadCount = async () => {
    try {
      const r = await api.get("/orders");
      setOrderCount(r.data.length);
    } catch { setOrderCount(0); }
  };
  useEffect(() => { loadCount(); }, []);

  const wipeOrders = async () => {
    if (!isSuperAdmin) {
      toast.error("Only Super Admin can perform this action");
      return;
    }
    if (confirmText !== "WIPE_ORDERS") {
      toast.error('Type WIPE_ORDERS exactly to confirm');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/maintenance/reset-orders", { confirm: "WIPE_ORDERS" });
      toast.success(`Deleted ${r.data.deleted_count} order${r.data.deleted_count === 1 ? "" : "s"}`);
      setConfirmText("");
      loadCount();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  if (!isSuperAdmin) {
    return (
      <div data-testid="maintenance-locked" className="rounded-2xl border border-zinc-200 bg-white p-10 text-center">
        <ShieldAlert size={36} className="mx-auto text-zinc-300 mb-3" />
        <h1 className="font-display text-xl font-bold mb-1">Super Admin only</h1>
        <p className="text-sm text-zinc-500">Destructive maintenance actions are restricted.</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-maintenance">
      <div className="overline text-zinc-500">System</div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-2">Maintenance</h1>
      <p className="text-sm text-zinc-500 mb-8">
        One-off destructive operations that are restricted to the Super Admin account. Be careful.
      </p>

      {/* Danger zone */}
      <section className="rounded-2xl border-2 border-red-200 bg-red-50/50 p-5 sm:p-6" data-testid="danger-zone">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h2 className="font-display font-bold text-lg">Reset all orders</h2>
            <p className="text-sm text-zinc-600 mt-1">
              This permanently deletes <strong>every</strong> order in the database — including their
              status history, delivery details and customer-facing receipts. Customer accounts,
              reviews, products, coupons and analytics-friendly logs are <strong>not</strong> touched.
              Use this only when you're switching from test orders to going live.
            </p>
            <p className="text-sm text-zinc-600 mt-2">
              There are currently <strong data-testid="order-count">{orderCount ?? "…"}</strong> order{orderCount === 1 ? "" : "s"} in the database.
            </p>
          </div>
        </div>

        <label className="text-xs font-semibold text-zinc-600 block mb-1">
          Type <code className="bg-zinc-200 px-1.5 py-0.5 rounded text-[11px]">WIPE_ORDERS</code> to enable the button
        </label>
        <input value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="WIPE_ORDERS"
                className="w-full sm:w-72 border border-zinc-300 rounded-lg px-3 py-2 text-sm font-mono"
                data-testid="wipe-confirm-input" />

        <div className="mt-4">
          <button
            onClick={wipeOrders}
            disabled={busy || confirmText !== "WIPE_ORDERS"}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-red-600 text-white font-semibold text-sm hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="wipe-orders-btn">
            <Trash2 size={14} /> {busy ? "Wiping…" : "Permanently delete all orders"}
          </button>
        </div>
      </section>
    </div>
  );
}
